// Talent, as the Claude Design draws it (screens_talent.jsx, owner 2026-10-15:
// "exactly as it, without removing existing things"). A page about each
// person's work: four tiles, the roster, and the person's week, utilisation,
// skills and — for HR alone — pay. Every figure is read, never typed in, and is
// "—" when there is nothing to read:
//   · the trend tiles come from the weekly snapshots (capacity_trend), whose
//     gate is capacity_overview's: the people the viewer may see;
//   · a colleague's free hours only as Capacity shows them (capacity_overview),
//     and their logged time only where the viewer can read it (time_read: their
//     own, or timesheets:full) — otherwise nothing is drawn, not an empty week;
//   · pay (salary burn, ₹/mo, Compensation) only at people:full, which is also
//     what comp_read lets through; pay is still edited on the People record;
//   · skills rated only by someone above the person or by HR (skills_write);
//     the controls show for them alone, and RLS still has the last word;
//   · no client logins and no access-role badges.
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../lib/auth';
import { can, canOpen } from '../lib/roles';
import { useOrgDates } from '../lib/orgSettings';
import { personDay } from '../lib/orgHours';
import { isWorkday, workingDayList, type Workdays } from '../lib/workdays';
import { listHolidays } from '../lib/holidays';
import {
  addDays,
  isoDay,
  monthAbbr,
  orgToday,
  weekKeys,
  weekStart,
  weekdayAbbr,
} from '../lib/dates';
import { withDepartmentName, type DepartmentEmbed } from '../lib/departments';
import { write } from '../lib/write';
import {
  LEVELS,
  LEVEL_IDS,
  avgHoursPerPerson,
  fetchCapacity,
  fetchCapacityTrend,
  hrs,
  utilisationPct,
  weekUtilisation,
  type CapacityPerson,
  type CapacityTrendWeek,
  type SkillLevel,
} from '../lib/capacity';
import {
  Avatar,
  Badge,
  Button,
  Icon,
  Mono,
  Progress,
  Select,
  TONE,
  markColor,
} from '../components/primitives';
import { KpiGrid, KpiTile } from '../components/domain';
import { Card, SegmentedControl } from '../components/card';
import { EmptyState, LoadError, useToast } from '../components/feedback';
import { OnTheClockCard } from '../components/onTheClock';

type Person = {
  id: string;
  full_name: string | null;
  title: string | null;
  department: string | null;
  role_id: string | null;
  status: string | null;
  joined: string | null;
  contract_hrs_per_day: number | null;
  employee_no: number | null;
};
type Held = {
  id: string;
  profile_id: string;
  skill_id: string | null;
  name: string;
  level: SkillLevel;
};
type HeldRow = Omit<Held, 'name'> & {
  skill: string;
  skills: { name: string; archived: boolean } | null;
};
type OrgSkill = { id: string; name: string };
type Alloc = {
  user_id: string;
  work_date: string;
  project_id: string | null;
  seconds: number;
  worked_seconds: number;
};
type Util = { pct: number; allocated: number; standard: number };

const UTIL_WEEKS = 4;
const TREND_WEEKS = 12;
const meta: CSSProperties = { fontSize: 'var(--hf-text-2xs)', color: 'var(--hf-ink-3)' };
const eyebrow: CSSProperties = {
  fontSize: 'var(--hf-text-2xs)',
  color: 'var(--hf-ink-3)',
  textTransform: 'uppercase',
  letterSpacing: '0.08em',
};

/** The design's rupees: ₹6.8L, ₹92K, ₹1.25Cr. */
const rupees = (n: number): string =>
  n >= 1e7
    ? `₹${(n / 1e7).toFixed(2)}Cr`
    : n >= 1e5
      ? `₹${(n / 1e5).toFixed(1)}L`
      : n >= 1e3
        ? `₹${(n / 1e3).toFixed(0)}K`
        : `₹${Math.round(n)}`;

/** Red from 100%, amber from 85%, sage below (the design's utilTone). */
const utilTone = (pct: number): 'rose' | 'amber' | 'sage' =>
  pct >= 100 ? 'rose' : pct >= 85 ? 'amber' : 'sage';
/** A colour at a strength, for the design's `${hex}14`-style tints of a token. */
const tint = (c: string, pct: number) => `color-mix(in srgb, ${c} ${pct}%, transparent)`;

/**
 * The Utilization ring's figures: project hours this week against the hours
 * the person is there (standard less approved leave). Null where a side is not
 * known — the ring then shows "—", never a 0.
 */
const ringFigures = (assigned: number | null, available: number | null) => {
  const pct = assigned != null && available ? Math.round((assigned / available) * 100) : null;
  return {
    pct,
    free: assigned != null && available != null ? Math.max(0, available - assigned) : null,
    overtime: pct != null && pct > 100 ? assigned! - available! : null,
  };
};

// The design's short week: "11–17 Oct", or "28 Sep – 4 Oct" across a month.
const weekSpan = (a: string, b: string) =>
  monthAbbr(a) === monthAbbr(b)
    ? `${+a.slice(8)}–${+b.slice(8)} ${monthAbbr(b)}`
    : `${+a.slice(8)} ${monthAbbr(a)} – ${+b.slice(8)} ${monthAbbr(b)}`;

// ISO week number of a day key, for the timesheet's "week 42".
const isoWeek = (key: string) => {
  const d = new Date(`${key}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7));
  return Math.ceil(((+d - Date.UTC(d.getUTCFullYear(), 0, 1)) / 86_400_000 + 1) / 7);
};

/** The design's "calculated" tag on a figure worked out from logged time. */
const LockBadge = () => (
  <span
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 4,
      fontSize: 'var(--hf-text-2xs)',
      color: 'var(--hf-ink-3)',
      fontFamily: 'var(--hf-font-mono)',
      padding: '2px 6px',
      borderRadius: 4,
      background: 'var(--hf-surface-2)',
      border: '1px solid var(--hf-rule-2)',
    }}
  >
    <Icon name="lock" size={9} />
    calculated
  </span>
);

const Section = ({
  title,
  locked,
  badge,
  right,
  children,
  style,
}: {
  title: string;
  locked?: boolean;
  badge?: ReactNode;
  right?: ReactNode;
  children?: ReactNode;
  style?: CSSProperties;
}) => (
  <Card
    label={title}
    level={3}
    badge={
      (locked || badge) && (
        <>
          {locked && <LockBadge />}
          {badge}
        </>
      )
    }
    right={right}
    style={{ padding: '18px 20px', ...style }}
  >
    {children}
  </Card>
);

// ── Utilization ring ──────────────────────────────────────────────────────────
const UtilRing = ({
  assigned,
  available,
}: {
  assigned: number | null;
  available: number | null;
}) => {
  const { pct, free, overtime } = ringFigures(assigned, available);
  const r = 38;
  const circ = 2 * Math.PI * r;
  const dash = pct == null ? 0 : Math.min(pct / 100, 1) * circ;
  const h = (n: number | null) => (n == null ? '—' : `${hrs(n)}h`);
  const items = [
    { l: 'Assigned', v: h(assigned), c: 'var(--hf-ink)' },
    { l: 'Available', v: h(available), c: 'var(--hf-ink-2)' },
    { l: 'Free', v: h(free), c: free == null ? 'var(--hf-ink-3)' : TONE.sage.fg },
    ...(overtime != null ? [{ l: 'Overtime', v: h(overtime), c: TONE.rose.fg }] : []),
  ];
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 22, flexWrap: 'wrap' }}>
      <svg
        width="96"
        height="96"
        viewBox="0 0 96 96"
        role="img"
        aria-label={pct == null ? 'Utilisation this week: no figure' : `${pct}% utilised this week`}
        style={{ flexShrink: 0 }}
      >
        <circle cx="48" cy="48" r={r} fill="none" stroke="var(--hf-rule-2)" strokeWidth="7" />
        {pct != null && (
          <circle
            cx="48"
            cy="48"
            r={r}
            fill="none"
            stroke={markColor(utilTone(pct))}
            strokeWidth="7"
            strokeDasharray={`${dash} ${circ}`}
            strokeLinecap="round"
            transform="rotate(-90 48 48)"
            style={{ transition: 'stroke-dasharray 600ms ease' }}
          />
        )}
        <text
          x="48"
          y="44"
          textAnchor="middle"
          fill={pct == null ? 'var(--hf-ink-3)' : 'var(--hf-ink)'}
          fontFamily="var(--hf-font-display)"
          fontSize="19"
          fontWeight="400"
        >
          {pct == null ? '—' : `${pct}%`}
        </text>
        <text
          x="48"
          y="60"
          textAnchor="middle"
          fill="var(--hf-ink-3)"
          fontFamily="var(--hf-font-mono)"
          fontSize="11"
        >
          util
        </text>
      </svg>
      <dl style={{ display: 'flex', flexDirection: 'column', gap: 10, margin: 0 }}>
        {items.map((it) => (
          <div key={it.l}>
            <dt style={{ ...eyebrow, marginBottom: 1 }}>{it.l}</dt>
            <dd
              style={{
                margin: 0,
                fontFamily: 'var(--hf-font-mono)',
                fontSize: 15,
                color: it.c,
                letterSpacing: '-0.01em',
              }}
            >
              {it.v}
            </dd>
          </div>
        ))}
      </dl>
    </div>
  );
};

// ── Timesheet: hours logged each day this week, from time_allocation ────────
const TimesheetChart = ({
  days,
  worked,
  perDay,
  expected,
  workdays,
}: {
  days: string[];
  worked: number[];
  perDay: number;
  expected: number;
  workdays: Workdays | null;
}) => {
  const off = days.map((d) => !isWorkday(d, workdays));
  const total = worked.reduce((a, b) => a + b, 0);
  const weekday = worked.reduce((a, b, i) => a + (off[i] ? 0 : b), 0);
  const weekend = total - weekday;
  const overtime = Math.max(0, total - expected);
  const maxBar = Math.max(perDay * 1.25, ...worked);
  // The design's "Mon–Fri", unless the org works other days.
  const workLabel =
    days
      .filter((_, i) => !off[i])
      .map(weekdayAbbr)
      .join() === 'Mon,Tue,Wed,Thu,Fri'
      ? 'Mon–Fri'
      : 'Workdays';
  const cells = [
    { l: 'Total', v: `${total.toFixed(1)}h`, c: 'var(--hf-ink)' },
    { l: workLabel, v: `${weekday.toFixed(1)}h`, c: 'var(--hf-ink-2)' },
    {
      l: 'Weekend',
      v: weekend > 0 ? `${weekend.toFixed(1)}h` : '—',
      c: weekend > 0 ? TONE.amber.fg : 'var(--hf-ink-3)',
    },
    {
      l: 'Overtime',
      v: overtime > 0 ? `+${overtime.toFixed(1)}h` : '—',
      c: overtime > 0 ? TONE.rose.fg : 'var(--hf-ink-3)',
    },
  ];
  return (
    <div>
      <div
        role="list"
        aria-label="Hours logged each day"
        style={{
          display: 'grid',
          gridTemplateColumns: `repeat(${days.length}, minmax(0, 1fr))`,
          gap: 6,
          marginBottom: 10,
        }}
      >
        {days.map((d, i) => {
          const h = worked[i];
          const over = h > perDay + 0.5;
          const bar = off[i]
            ? 'var(--hf-surface-3)'
            : over
              ? 'var(--hf-amber)'
              : 'var(--hf-accent)';
          return (
            <div
              key={d}
              role="listitem"
              aria-label={`${weekdayAbbr(d)} ${hrs(h)}h`}
              style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}
            >
              <span
                aria-hidden
                style={{
                  fontFamily: 'var(--hf-font-mono)',
                  fontSize: 'var(--hf-text-2xs)',
                  color: h > 0 ? (over ? TONE.amber.fg : 'var(--hf-ink)') : 'transparent',
                  minHeight: 14,
                }}
              >
                {h > 0 ? hrs(h) : '·'}
              </span>
              <span
                aria-hidden
                style={{
                  display: 'block',
                  width: '100%',
                  height: 60,
                  borderRadius: 5,
                  background: 'var(--hf-surface-2)',
                  overflow: 'hidden',
                  position: 'relative',
                }}
              >
                <span
                  style={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    height: `${(h / maxBar) * 100}%`,
                    borderRadius: '4px 4px 0 0',
                    background: bar,
                    opacity: off[i] && h === 0 ? 0.25 : 1,
                  }}
                />
              </span>
              <span
                aria-hidden
                style={{
                  fontSize: 'var(--hf-text-2xs)',
                  color: off[i] ? 'var(--hf-ink-3)' : 'var(--hf-ink-2)',
                }}
              >
                {weekdayAbbr(d)}
              </span>
            </div>
          );
        })}
      </div>
      <dl
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 6rem), 1fr))',
          gap: 8,
          margin: 0,
        }}
      >
        {cells.map((it) => (
          <div
            key={it.l}
            style={{
              padding: '7px 10px',
              borderRadius: 6,
              background: 'var(--hf-surface)',
              border: '1px solid var(--hf-rule)',
            }}
          >
            <dt style={{ ...eyebrow, marginBottom: 2 }}>{it.l}</dt>
            <dd style={{ margin: 0, fontFamily: 'var(--hf-font-mono)', fontSize: 13, color: it.c }}>
              {it.v}
            </dd>
          </div>
        ))}
      </dl>
    </div>
  );
};

// ── Skills: the design's chips and level bars; the controls for raters ──────
const SkillPill = ({
  h,
  canRate,
  editing,
  onEdit,
  onRemove,
}: {
  h: Pick<Held, 'name' | 'level'>;
  canRate?: boolean;
  editing?: boolean;
  onEdit?: () => void;
  onRemove?: () => void;
}) => {
  const l = LEVELS[h.level] ?? LEVELS[2];
  const c = l.color;
  const seg: CSSProperties = {
    padding: '5px 8px',
    fontSize: 'var(--hf-text-2xs)',
    fontFamily: 'var(--hf-font-mono)',
    color: c,
    background: tint(c, 13),
    border: 0,
    borderLeft: `1px solid ${tint(c, 20)}`,
  };
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'stretch',
        borderRadius: 999,
        background: tint(c, 8),
        border: `1px solid ${tint(c, editing ? 45 : 20)}`,
        overflow: 'hidden',
      }}
    >
      <span style={{ padding: '5px 10px 5px 11px', fontSize: 12, color: c, fontWeight: 500 }}>
        {h.name}
      </span>
      {canRate && onEdit ? (
        <button
          type="button"
          aria-expanded={editing}
          aria-label={`${h.name}: ${l.label}. Change level`}
          onClick={onEdit}
          style={{
            ...seg,
            cursor: 'pointer',
            borderBottom: `1px dashed ${tint(c, 40)}`,
          }}
        >
          {l.label}
        </button>
      ) : (
        <span style={seg}>{l.label}</span>
      )}
      {canRate && onRemove && (
        <button
          type="button"
          aria-label={`Remove ${h.name}`}
          title={`Remove ${h.name}`}
          onClick={onRemove}
          style={{
            padding: '5px 9px',
            background: 'transparent',
            border: 0,
            borderLeft: `1px solid ${tint(c, 13)}`,
            color: c,
            opacity: 0.6,
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
          }}
        >
          <Icon name="x" size={9} />
        </button>
      )}
    </span>
  );
};

const formBox: CSSProperties = {
  marginBottom: 16,
  padding: '12px 14px',
  borderRadius: 8,
  background: 'var(--hf-surface)',
  border: '1px solid var(--hf-rule-2)',
  display: 'flex',
  flexWrap: 'wrap',
  gap: 10,
  alignItems: 'center',
};
const levelOptions = LEVEL_IDS.map((l) => ({ value: String(l), label: LEVELS[l].label }));

const SkillsPanel = ({
  person,
  held,
  orgSkills,
  canRate,
  onChanged,
}: {
  person: Person;
  held: Held[];
  orgSkills: OrgSkill[];
  canRate: boolean;
  onChanged: () => Promise<void>;
}) => {
  const { push } = useToast();
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [pick, setPick] = useState('');
  const [level, setLevel] = useState<SkillLevel>(2);
  const who = person.full_name ?? 'this person';
  const refused = `You can’t rate ${who}’s skills: only someone above them in the reporting line, or HR, can.`;

  const run = async (what: string, act: () => Promise<string | undefined>) => {
    setBusy(true);
    const error = await act();
    await onChanged();
    setBusy(false);
    if (error) push({ tone: 'error', message: `${what} wasn’t saved. ${error}` });
  };
  const rate = (h: Held, l: SkillLevel) =>
    run(`${h.name}’s level`, async () => {
      if (!supabase) return;
      const r = await write(supabase.from('employee_skills').update({ level: l }).eq('id', h.id));
      return r.ok ? undefined : r.reason === 'refused' ? refused : r.error.message;
    });
  const remove = (h: Held) =>
    run(`Removing ${h.name}`, async () => {
      if (!supabase) return;
      const r = await write(supabase.from('employee_skills').delete().eq('id', h.id));
      return r.ok ? undefined : r.reason === 'refused' ? refused : r.error.message;
    });
  const add = (s: OrgSkill) =>
    run(`Adding ${s.name}`, async () => {
      if (!supabase) return;
      const { error } = await supabase.from('employee_skills').insert({
        profile_id: person.id,
        skill_id: s.id,
        skill: s.name,
        level,
      });
      setPick('');
      setLevel(2);
      setAdding(false);
      // 42501: skills_write's WITH CHECK refused the row.
      return !error ? undefined : error.code === '42501' ? refused : error.message;
    });

  const free = orgSkills.filter((s) => !held.some((h) => h.skill_id === s.id));
  const chosen = free.find((s) => s.id === pick);
  const open = held.find((h) => h.id === editing);
  const quiet: CSSProperties = {
    padding: '6px 10px',
    borderRadius: 6,
    background: 'transparent',
    border: '1px solid var(--hf-rule-2)',
    color: 'var(--hf-ink-3)',
    fontSize: 12,
    fontFamily: 'inherit',
    cursor: 'pointer',
  };
  return (
    <fieldset disabled={busy} style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7, marginBottom: 16 }}>
        {held.length === 0 && (
          <span style={{ ...meta, alignSelf: 'center' }}>No skills rated yet.</span>
        )}
        {held.map((h) => (
          <SkillPill
            key={h.id}
            h={h}
            canRate={canRate}
            editing={editing === h.id}
            onEdit={() => setEditing((e) => (e === h.id ? null : h.id))}
            onRemove={() => void remove(h)}
          />
        ))}
        {canRate && (
          <button
            type="button"
            aria-expanded={adding}
            onClick={() => setAdding((a) => !a)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 5,
              padding: '5px 11px',
              borderRadius: 999,
              background: 'transparent',
              border: '1px dashed var(--hf-rule-2)',
              color: 'var(--hf-ink-3)',
              fontSize: 'var(--hf-text-2xs)',
              cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >
            <Icon name="plus" size={10} />
            Add skill
          </button>
        )}
      </div>

      {canRate && open && (
        <div style={formBox}>
          <span style={{ fontSize: 12, color: 'var(--hf-ink)' }}>{open.name}</span>
          <SegmentedControl
            label={`${open.name} level`}
            value={String(open.level)}
            options={levelOptions}
            onChange={(v) => void rate(open, Number(v) as SkillLevel)}
          />
          <button type="button" style={quiet} onClick={() => setEditing(null)}>
            Done
          </button>
        </div>
      )}

      {canRate && adding && (
        <div style={formBox}>
          {free.length > 0 ? (
            <>
              <div style={{ minWidth: 170 }}>
                <Select
                  ariaLabel="Skill to add"
                  placeholder="Pick a skill…"
                  value={pick}
                  options={free.map((s) => ({ value: s.id, label: s.name }))}
                  onChange={setPick}
                />
              </div>
              <span style={{ ...meta, fontFamily: 'var(--hf-font-mono)' }}>level</span>
              <SegmentedControl
                label="Level of the new skill"
                value={String(level)}
                options={levelOptions}
                onChange={(v) => setLevel(Number(v) as SkillLevel)}
              />
              {chosen && <SkillPill h={{ name: chosen.name, level }} />}
              <Button
                size="sm"
                variant="accent"
                disabled={!chosen}
                onClick={() => chosen && void add(chosen)}
              >
                Add
              </Button>
            </>
          ) : (
            <span style={meta}>
              {orgSkills.length === 0
                ? 'The skill list is empty. The owner or an admin adds skills in Organisation settings.'
                : `${who} has every skill on the org’s list.`}
            </span>
          )}
          <button
            type="button"
            style={quiet}
            onClick={() => {
              setAdding(false);
              setPick('');
            }}
          >
            Cancel
          </button>
        </div>
      )}

      {held.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
          {held.map((h) => {
            const l = LEVELS[h.level] ?? LEVELS[2];
            return (
              <div
                key={h.id}
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'minmax(0, 130px) 1fr 64px',
                  alignItems: 'center',
                  gap: 10,
                }}
              >
                <span
                  style={{
                    fontSize: 12,
                    color: 'var(--hf-ink-2)',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {h.name}
                </span>
                {/* Four steps, one per level: never a %. */}
                <span
                  aria-hidden
                  style={{
                    display: 'block',
                    height: 4,
                    borderRadius: 999,
                    background: 'var(--hf-rule-2)',
                    overflow: 'hidden',
                  }}
                >
                  <span
                    style={{
                      display: 'block',
                      width: `${(h.level / 4) * 100}%`,
                      height: '100%',
                      background: l.color,
                      borderRadius: 999,
                    }}
                  />
                </span>
                <span
                  style={{
                    fontFamily: 'var(--hf-font-mono)',
                    fontSize: 'var(--hf-text-2xs)',
                    color: l.color,
                    textAlign: 'right',
                  }}
                >
                  {l.label}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </fieldset>
  );
};

// ── Compensation: people:full alone; edited on the People record ────────────
const CompCard = ({
  label,
  value,
  calculated,
  action,
}: {
  label: string;
  value: string;
  calculated?: boolean;
  action?: ReactNode;
}) => (
  <div
    style={{
      padding: '16px 18px',
      borderRadius: 10,
      background: 'var(--hf-surface)',
      border: `1px solid ${calculated ? 'var(--hf-rule)' : 'var(--hf-rule-2)'}`,
      minWidth: 0,
    }}
  >
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: calculated ? 'flex-start' : 'space-between',
        gap: 5,
        marginBottom: 8,
      }}
    >
      <div style={eyebrow}>{label}</div>
      {calculated && <LockBadge />}
      {action}
    </div>
    <div
      style={{
        fontFamily: 'var(--hf-font-display)',
        fontSize: 26,
        fontWeight: 400,
        color: calculated ? 'var(--hf-ink-2)' : 'var(--hf-ink)',
        letterSpacing: '-0.02em',
      }}
    >
      {value}
    </div>
  </div>
);

// This org week, the four whole weeks before it for utilisation, and the
// calendar year for a pay-per-hour divisor. Keys only.
const periods = (weekStartsOn: number, tz: string) => {
  const today = orgToday(tz);
  const ws = weekStart(weekStartsOn, today);
  const y = today.getFullYear();
  const weekEnd = isoDay(addDays(ws, 6));
  return {
    week: weekKeys(weekStartsOn, today),
    utilFrom: isoDay(addDays(ws, -7 * UTIL_WEEKS)),
    utilTo: isoDay(addDays(ws, -1)),
    weekEnd,
    yearFrom: `${y}-01-01`,
    yearTo: `${y}-12-31`,
  };
};

const change = (now: number | null, before: number | null, dp: number) => {
  if (now == null || before == null) return {};
  const d = Math.round((now - before) * 10 ** dp) / 10 ** dp;
  return {
    delta: `${d > 0 ? '+' : d < 0 ? '−' : ''}${Math.abs(d).toFixed(dp)}`,
    trend: d > 0 ? ('up' as const) : d < 0 ? ('down' as const) : ('neutral' as const),
  };
};
// A sparkline needs two points to be a line.
const series = (v: (number | null)[]) => (v.filter((x) => x != null).length >= 2 ? v : undefined);

export function TalentScreen() {
  const { perms, session, profile } = useAuth();
  const { weekStartsOn, tz, workdays, hours } = useOrgDates();
  const navigate = useNavigate();
  const me = session?.user.id ?? profile?.id;
  const seesAllTime = can(perms, 'timesheets', 'full');
  // Pay: people:full, the same line comp_read draws (UI only; RLS decides).
  const peopleFull = can(perms, 'people', 'full');

  const [people, setPeople] = useState<Person[] | null>(supabase ? null : []);
  const [failed, setFailed] = useState(false);
  const [held, setHeld] = useState<Held[]>([]);
  const [orgSkills, setOrgSkills] = useState<OrgSkill[]>([]);
  const [capacity, setCapacity] = useState<Map<string, CapacityPerson>>(new Map());
  const [trend, setTrend] = useState<CapacityTrendWeek[]>([]);
  const [comp, setComp] = useState<Map<string, number> | null>(null);
  const [alloc, setAlloc] = useState<Alloc[]>([]);
  const [holidays, setHolidays] = useState<Set<string> | null>(null);
  const [selId, setSelId] = useState<string | null>(null);
  const [rater, setRater] = useState<{ id: string; ok: boolean } | null>(null);

  const { week, utilFrom, utilTo, weekEnd, yearFrom, yearTo } = periods(weekStartsOn, tz);

  const loadSkills = useCallback(async () => {
    if (!supabase) return;
    const { data } = await supabase
      .from('employee_skills')
      .select('id, profile_id, skill_id, skill, level, skills(name, archived)')
      .order('level', { ascending: false });
    setHeld(
      ((data as unknown as HeldRow[] | null) ?? [])
        .filter((r) => !r.skills?.archived)
        .map((r) => ({ ...r, name: r.skills?.name ?? r.skill })),
    );
  }, []);

  // A counter, not a callback: Retry bumps it and the effect reads again.
  const [reloads, setReloads] = useState(0);
  useEffect(() => {
    if (!supabase) return;
    const sb = supabase;
    let live = true;
    void (async () => {
      const [ppl, cap, time, hol, cat, tr, pay] = await Promise.all([
        sb
          .from('profiles')
          .select(
            'id, full_name, title, role_id, status, joined, contract_hrs_per_day, employee_no, departments(name)',
          )
          .order('full_name'),
        fetchCapacity(1),
        sb
          .from('time_allocation')
          .select('user_id, work_date, project_id, seconds, worked_seconds')
          .gte('work_date', utilFrom)
          .lte('work_date', weekEnd),
        listHolidays(
          utilFrom < yearFrom ? utilFrom : yearFrom,
          weekEnd > yearTo ? weekEnd : yearTo,
        ),
        sb.from('skills').select('id, name').eq('archived', false).order('name'),
        fetchCapacityTrend(TREND_WEEKS),
        peopleFull
          ? sb.from('employee_compensation').select('profile_id, annual_ctc')
          : Promise.resolve({ data: null, error: null }),
        loadSkills(),
      ]);
      if (!live) return;
      setFailed(!!ppl.error);
      if (ppl.error) return;
      type Row = Omit<Person, 'department'> & DepartmentEmbed;
      const roster = ((ppl.data as unknown as Row[]) ?? [])
        .map(withDepartmentName)
        // Not client logins, and not people who have left (DESIGN-SYNC, Talent).
        .filter((p) => p.role_id !== 'client' && p.status === 'active');
      setPeople(roster);
      setCapacity(new Map(cap.data.map((c) => [c.profile_id, c])));
      setAlloc((time.data as Alloc[] | null) ?? []);
      setHolidays(hol.error ? null : new Set(hol.data.map((h) => h.date)));
      setOrgSkills((cat.data as OrgSkill[] | null) ?? []);
      setTrend(tr);
      setComp(
        peopleFull && !pay.error
          ? new Map(
              ((pay.data as { profile_id: string; annual_ctc: number | null }[] | null) ?? []).map(
                (r) => [r.profile_id, Number(r.annual_ctc ?? 0)],
              ),
            )
          : null,
      );
      setSelId((prev) => prev ?? roster.find((p) => p.id === me)?.id ?? roster[0]?.id ?? null);
    })();
    return () => {
      live = false;
    };
  }, [reloads, loadSkills, me, utilFrom, utilTo, weekEnd, yearFrom, yearTo, peopleFull]);

  // May the viewer rate this person? HR (people:full) may rate anyone; anyone
  // else only someone below them — is_above, the helper skills_write uses.
  useEffect(() => {
    if (!supabase || !selId || peopleFull) return;
    let live = true;
    void supabase.rpc('is_above', { p_profile: selId }).then(({ data }) => {
      if (live) setRater({ id: selId, ok: data === true });
    });
    return () => {
      live = false;
    };
  }, [selId, peopleFull]);
  const canRate = peopleFull || (rater?.id === selId && rater.ok);

  // Logged time is readable for yourself, or with timesheets:full (time_read).
  const readable = useCallback((id: string) => seesAllTime || id === me, [seesAllTime, me]);
  const utilOf = useMemo(() => {
    const out = new Map<string, Util>();
    if (!people || !holidays) return out;
    for (const p of people) {
      if (!readable(p.id)) continue;
      const from = p.joined && p.joined > utilFrom ? p.joined : utilFrom;
      const standard =
        personDay(p.contract_hrs_per_day, hours) *
        workingDayList(from, utilTo, workdays, holidays).length;
      const allocated =
        alloc
          .filter(
            (r) =>
              r.user_id === p.id && r.project_id && r.work_date >= from && r.work_date <= utilTo,
          )
          .reduce((a, r) => a + r.seconds, 0) / 3600;
      const pct = utilisationPct(allocated, standard);
      if (pct != null) out.set(p.id, { pct, allocated, standard });
    }
    return out;
  }, [people, holidays, alloc, readable, utilFrom, utilTo, workdays, hours]);

  const page: CSSProperties = {
    padding: '20px 24px',
    display: 'flex',
    flexDirection: 'column',
    gap: 14,
  };
  if (failed)
    return (
      <div style={page}>
        <LoadError what="the talent roster" onRetry={() => setReloads((n) => n + 1)} />
      </div>
    );
  if (!people)
    return (
      <div style={page}>
        <div role="status" style={{ color: 'var(--hf-ink-3)' }}>
          Loading talent…
        </div>
      </div>
    );

  const skillsOf = (id: string) => held.filter((h) => h.profile_id === id);
  const sel = people.find((p) => p.id === selId);

  // The tiles: the last finished week against the one before, from the
  // snapshots; salary burn is CTC ÷ 12 over the roster.
  const utilWeeks = trend.map(weekUtilisation);
  const hrsWeeks = trend.map(avgHoursPerPerson);
  const lastUtil = utilWeeks.at(-1) ?? null;
  const lastHrs = hrsWeeks.at(-1) ?? null;
  const burn = comp ? people.reduce((a, p) => a + (comp.get(p.id) ?? 0), 0) / 12 : null;

  return (
    <div style={page}>
      <KpiGrid>
        <KpiTile
          label="Avg utilization"
          value={lastUtil}
          unit="%"
          deltaAt="head"
          {...change(lastUtil, utilWeeks.at(-2) ?? null, 0)}
          tone="sage"
          data={series(utilWeeks)}
        />
        <KpiTile
          label="Avg hrs / week"
          value={lastHrs?.toFixed(1) ?? null}
          deltaAt="head"
          {...change(lastHrs, hrsWeeks.at(-2) ?? null, 1)}
          tone="sky"
          data={series(hrsWeeks)}
        />
        {peopleFull && (
          <KpiTile
            label="Monthly salary burn"
            value={burn == null ? null : rupees(burn)}
            tone="amber"
            variant="bar"
            data={burn ? [burn] : undefined}
          />
        )}
        <KpiTile
          label="Headcount"
          value={people.length}
          tone="sage"
          variant="ring"
          data={[people.length, people.length]}
        />
      </KpiGrid>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, alignItems: 'stretch' }}>
        <Card
          style={{
            flex: '1 1 272px',
            maxWidth: '100%',
            padding: 0,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            alignSelf: 'flex-start',
          }}
        >
          <div
            style={{
              padding: '10px 14px 9px',
              borderBottom: '1px solid var(--hf-rule)',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            <span style={{ ...eyebrow, fontWeight: 500, letterSpacing: '0.09em' }}>Roster</span>
            <Mono dim style={{ fontSize: 'var(--hf-text-2xs)' }}>
              {people.length}
            </Mono>
          </div>
          {people.length === 0 ? (
            <div style={{ padding: 14 }}>
              <EmptyState title="Nobody here yet">People appear as they are added.</EmptyState>
            </div>
          ) : (
            <nav aria-label="Talent roster" className="hf-scroll" style={{ overflow: 'auto' }}>
              {people.map((p) => {
                const u = utilOf.get(p.id);
                const mine = skillsOf(p.id);
                const on = p.id === selId;
                const ctc = comp?.get(p.id);
                return (
                  <button
                    key={p.id}
                    type="button"
                    aria-current={on ? 'true' : undefined}
                    onClick={() => setSelId(p.id)}
                    style={{
                      display: 'block',
                      width: '100%',
                      padding: '10px 14px 10px 12px',
                      textAlign: 'left',
                      border: 0,
                      borderBottom: '1px solid var(--hf-rule)',
                      borderLeft: `2px solid ${on ? 'var(--hf-accent)' : 'transparent'}`,
                      background: on ? 'var(--hf-accent-wash)' : 'transparent',
                      color: 'inherit',
                      font: 'inherit',
                      cursor: 'pointer',
                    }}
                  >
                    <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <Avatar name={p.full_name ?? undefined} size={30} />
                      <span style={{ flex: 1, minWidth: 0 }}>
                        <span
                          style={{
                            display: 'block',
                            fontSize: 'var(--hf-text-sm)',
                            color: 'var(--hf-ink)',
                            fontWeight: 500,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {p.full_name ?? '—'}
                        </span>
                        <span
                          style={{
                            display: 'block',
                            ...meta,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {p.title ?? p.department ?? '—'}
                        </span>
                      </span>
                      <span style={{ textAlign: 'right', flexShrink: 0 }}>
                        {u && (
                          <span
                            style={{
                              display: 'block',
                              fontFamily: 'var(--hf-font-mono)',
                              fontSize: 'var(--hf-text-2xs)',
                              color: TONE[utilTone(u.pct)].fg,
                            }}
                          >
                            {u.pct}%
                          </span>
                        )}
                        {ctc ? (
                          <span
                            style={{ display: 'block', ...meta, fontFamily: 'var(--hf-font-mono)' }}
                          >
                            {rupees(ctc / 12)}/mo
                          </span>
                        ) : null}
                      </span>
                    </span>
                    {u && (
                      <span style={{ display: 'block', marginTop: 7 }}>
                        <Progress
                          value={u.pct}
                          tone={utilTone(u.pct)}
                          size={3}
                          label={`${p.full_name ?? ''} utilisation`}
                          meter
                        />
                      </span>
                    )}
                    {mine.length > 0 && (
                      <span style={{ display: 'flex', gap: 3, marginTop: 6, flexWrap: 'wrap' }}>
                        {mine.slice(0, 3).map((h) => {
                          const l = LEVELS[h.level] ?? LEVELS[2];
                          return (
                            <span
                              key={h.id}
                              title={`${h.name} · ${l.label}`}
                              style={{
                                fontSize: 'var(--hf-text-2xs)',
                                padding: '1px 5px',
                                borderRadius: 3,
                                background: tint(l.color, 9),
                                color: l.color,
                                border: `1px solid ${tint(l.color, 16)}`,
                                fontFamily: 'var(--hf-font-mono)',
                              }}
                            >
                              {h.name}
                              <span className="hf-sr-only"> · {l.label}</span>
                            </span>
                          );
                        })}
                        {mine.length > 3 && (
                          <span
                            style={{
                              fontSize: 'var(--hf-text-2xs)',
                              padding: '1px 5px',
                              borderRadius: 3,
                              background: 'var(--hf-surface-2)',
                              color: 'var(--hf-ink-3)',
                              fontFamily: 'var(--hf-font-mono)',
                            }}
                          >
                            +{mine.length - 3}
                          </span>
                        )}
                      </span>
                    )}
                  </button>
                );
              })}
            </nav>
          )}
        </Card>

        {sel && (
          <PersonPanel
            person={sel}
            cap={capacity.get(sel.id)}
            util={utilOf.get(sel.id)}
            alloc={readable(sel.id) ? alloc.filter((r) => r.user_id === sel.id) : null}
            week={week}
            workdays={workdays}
            holidays={holidays}
            perDay={personDay(sel.contract_hrs_per_day, hours)}
            yearDays={holidays ? workingDayList(yearFrom, yearTo, workdays, holidays).length : 0}
            ctc={comp ? (comp.get(sel.id) ?? null) : undefined}
            skills={
              <SkillsPanel
                person={sel}
                held={skillsOf(sel.id)}
                orgSkills={orgSkills}
                canRate={canRate}
                onChanged={loadSkills}
              />
            }
            canRate={canRate}
            onSchedule={
              canOpen(perms, 'schedule') ? () => navigate(`/gantt?person=${sel.id}`) : undefined
            }
            onProfile={
              canOpen(perms, 'people')
                ? () => navigate(`/hr?view=details&person=${sel.id}`)
                : undefined
            }
          />
        )}
      </div>

      <OnTheClockCard />
    </div>
  );
}

const PersonPanel = ({
  person: p,
  cap,
  util,
  alloc,
  week,
  workdays,
  holidays,
  perDay,
  yearDays,
  ctc,
  skills,
  canRate,
  onSchedule,
  onProfile,
}: {
  person: Person;
  cap?: CapacityPerson;
  util?: Util;
  /** Their logged time, or null when the viewer may not read it. */
  alloc: Alloc[] | null;
  week: string[];
  workdays: Workdays | null;
  holidays: Set<string> | null;
  perDay: number;
  yearDays: number;
  /** Annual CTC; undefined below people:full, null when none is on record. */
  ctc?: number | null;
  skills: ReactNode;
  canRate: boolean;
  onSchedule?: () => void;
  onProfile?: () => void;
}) => {
  const now = cap?.weeks[0];
  const available = now ? now.standard_hours - now.away_hours : null;
  const assigned = alloc
    ? alloc
        .filter((r) => r.project_id && week.includes(r.work_date))
        .reduce((a, r) => a + r.seconds, 0) / 3600
    : null;
  const worked = alloc
    ? week.map(
        (d) =>
          Math.max(0, ...alloc.filter((r) => r.work_date === d).map((r) => r.worked_seconds)) /
          3600,
      )
    : null;
  const expected =
    available ??
    perDay * workingDayList(week[0], week[week.length - 1], workdays, holidays ?? undefined).length;
  const weekTotal = worked?.reduce((a, b) => a + b, 0) ?? 0;
  const perHour = ctc && perDay * yearDays ? ctc / (perDay * yearDays) : null;

  return (
    <div
      style={{
        flex: '999 1 24rem',
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
      }}
    >
      <Card
        style={{
          padding: '18px 22px',
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 16,
        }}
      >
        <Avatar name={p.full_name ?? undefined} size={52} />
        <div style={{ flex: '1 1 12rem', minWidth: 0 }}>
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: 8,
              marginBottom: 4,
            }}
          >
            <h2
              style={{
                margin: 0,
                fontFamily: 'var(--hf-font-display)',
                fontSize: 22,
                fontWeight: 400,
                letterSpacing: '-0.025em',
                color: 'var(--hf-ink)',
              }}
            >
              {p.full_name ?? '—'}
            </h2>
            {p.department && <Badge tone="neutral">{p.department}</Badge>}
            {p.role_id === 'contractor' && <Badge tone="neutral">Freelance</Badge>}
            {util && (
              <Badge tone={utilTone(util.pct)} dot>
                {util.pct}% utilised
              </Badge>
            )}
          </div>
          <div
            style={{
              fontSize: 12,
              color: 'var(--hf-ink-2)',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
            }}
          >
            <span>{p.title ?? 'No title yet'}</span>
            <span style={{ color: 'var(--hf-ink-3)' }}>·</span>
            <Mono style={{ fontSize: 'var(--hf-text-2xs)' }}>
              <span className="hf-sr-only">Employee number </span>
              {p.employee_no ?? '—'}
            </Mono>
          </div>
        </div>
        {(onSchedule || onProfile) && (
          <div style={{ display: 'flex', gap: 6 }}>
            {onSchedule && (
              <Button variant="glass" icon="calendar" onClick={onSchedule}>
                Schedule
              </Button>
            )}
            {onProfile && (
              <Button variant="glass" icon="file" onClick={onProfile}>
                Profile
              </Button>
            )}
          </div>
        )}
      </Card>

      {(now || worked) && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
          <Section
            title="Utilization"
            locked
            badge={
              <Mono dim style={{ fontSize: 'var(--hf-text-2xs)' }}>
                this week
              </Mono>
            }
            style={{ flex: '1 1 16rem' }}
          >
            <UtilRing assigned={assigned} available={available} />
            {assigned != null && available ? (
              <div style={{ marginTop: 14 }}>
                <Progress
                  value={(assigned / available) * 100}
                  tone={utilTone((assigned / available) * 100)}
                  size={5}
                  label={`${p.full_name ?? ''} utilisation this week`}
                  meter
                />
              </div>
            ) : null}
            {now && (
              <div style={{ ...meta, marginTop: 10 }}>
                Of {hrs(now.standard_hours)}h contracted: {hrs(now.away_hours)}h away,{' '}
                {hrs(now.tentative_hours)}h tentative (leave waiting for approval).
              </div>
            )}
            {util && (
              <div style={{ ...meta, marginTop: 4 }}>
                Last {UTIL_WEEKS} weeks: {hrs(util.allocated)}h on projects of {hrs(util.standard)}h
                standard.
              </div>
            )}
          </Section>

          {worked && (
            <Section
              title="Timesheet"
              locked
              badge={
                <Mono dim style={{ fontSize: 'var(--hf-text-2xs)' }}>
                  week {isoWeek(week[Math.min(1, week.length - 1)])} ·{' '}
                  {weekSpan(week[0], week[week.length - 1])}
                </Mono>
              }
              right={
                <Mono style={{ fontSize: 12, color: 'var(--hf-ink)' }}>
                  {weekTotal.toFixed(1)}h total
                </Mono>
              }
              style={{ flex: '1.7 1 22rem' }}
            >
              <TimesheetChart
                days={week}
                worked={worked}
                perDay={perDay}
                expected={expected}
                workdays={workdays}
              />
            </Section>
          )}
        </div>
      )}

      <Section
        title="Skills"
        badge={
          <span style={{ ...meta, fontFamily: 'var(--hf-font-mono)' }}>
            colour = level{canRate ? ' · click a level to change it' : ''}
          </span>
        }
      >
        {skills}
      </Section>

      {ctc !== undefined && (
        <Section title="Compensation" badge={<Badge tone="warning">confidential</Badge>}>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 11rem), 1fr))',
              gap: 12,
            }}
          >
            <CompCard
              label="Annual CTC"
              value={ctc ? rupees(ctc) : '—'}
              action={
                <Link
                  to={`/hr?view=details&person=${p.id}&tab=comp`}
                  aria-label={`Edit ${p.full_name ?? 'their'} pay on the People record`}
                  title="Edit on the People record"
                  style={{ color: 'var(--hf-ink-3)', display: 'inline-flex', padding: 2 }}
                >
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    aria-hidden
                  >
                    <path d="M11 5H6a2 2 0 0 0-2 2v11a2 2 0 0 0 2 2h11a2 2 0 0 0 2-2v-5" />
                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                  </svg>
                </Link>
              }
            />
            <CompCard label="Monthly" value={ctc ? rupees(ctc / 12) : '—'} calculated />
            <CompCard label="Cost / hr" value={perHour ? rupees(perHour) : '—'} calculated />
          </div>
        </Section>
      )}
    </div>
  );
};
